import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Sun, Cloud, CloudRain, Droplets, Wind } from 'lucide-react';

const Hero = ({ activeSection = 'hotel', openBooking, language }) => {
  const [weather, setWeather] = useState(null);

  const translations = {
    de: {
      hotel: {
        title: 'Hotel La Casa',
        subtitle: 'Eleganz & Komfort in Hattingen',
        bookRoom: 'Zimmer buchen'
      },
      restaurant: {
        title: 'Ristorante La Casa',
        subtitle: 'Authentisches italienisches Geschmackserlebnis'
      },
      weather: {
        feelsLike: 'Gefühlt',
        humidity: 'Luftfeuchtigkeit',
        wind: 'Wind'
      }
    },
    en: {
      hotel: {
        title: 'Hotel La Casa',
        subtitle: 'Elegance & Comfort in Hattingen',
        bookRoom: 'Book Room'
      },
      restaurant: {
        title: 'Ristorante La Casa',
        subtitle: 'Authentic Italian Dining Experience'
      },
      weather: {
        feelsLike: 'Feels like',
        humidity: 'Humidity',
        wind: 'Wind'
      }
    }
  };

  const t = translations[language];

  // Static content for each section - no slideshow
  const hotelContent = {
    image: '/pocetnaHotel.png',
    title: t.hotel.title,
    subtitle: t.hotel.subtitle,
  };

  const restaurantContent = {
    image: '/restoranPocetna.jpeg',
    title: t.restaurant.title,
    subtitle: t.restaurant.subtitle,
  };

  const currentContent = activeSection === 'hotel' ? hotelContent : restaurantContent;

  // Weather API Call
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=51.3989&lon=7.1864&appid=a1e559777fb3157b8e687aac176486e2&units=metric&lang=en`
        );
        
        if (!response.ok) throw new Error('API error');
        
        const data = await response.json();
        
        setWeather({
          temp: Math.round(data.main.temp),
          feelsLike: Math.round(data.main.feels_like),
          description: data.weather[0].description,
          humidity: data.main.humidity,
          windSpeed: Math.round(data.wind.speed * 3.6),
          icon: data.weather[0].main,
        });
      } catch (error) {
        console.error('Weather error:', error);
        setWeather({
          temp: 12,
          feelsLike: 10,
          description: 'partly cloudy',
          humidity: 68,
          windSpeed: 15,
          icon: 'Clouds',
        });
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 1800000);
    return () => clearInterval(interval);
  }, []);

  // No slideshow - content changes based on activeSection

  const getWeatherIcon = (size = 36) => {
    if (!weather) return <Sun className="text-luxury-gold" size={size} />;
    
    switch (weather.icon) {
      case 'Clear':
        return <Sun className="text-luxury-gold" size={size} />;
      case 'Clouds':
        return <Cloud className="text-zinc-300" size={size} />;
      case 'Rain':
      case 'Drizzle':
        return <CloudRain className="text-blue-400" size={size} />;
      default:
        return <Sun className="text-luxury-gold" size={size} />;
    }
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image - changes based on activeSection */}
      <motion.div
        key={activeSection} // Re-animate when section changes
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="absolute inset-0"
      >
        <img
          src={currentContent.image}
          alt={currentContent.title}
          className={`absolute inset-0 w-full h-full object-cover ${
            activeSection === 'restaurant' ? 'object-[center_30%]' : 'object-center'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
      </motion.div>

      {/* Weather Widget - DESKTOP (Large) */}
      {weather && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="hidden md:block absolute bottom-48 left-8 lg:left-10 z-30"
        >
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-br from-luxury-gold to-yellow-500 rounded-xl blur opacity-20" />
            
            <div className="relative bg-gradient-to-br from-black/75 via-zinc-900/75 to-black/75 backdrop-blur-md border border-luxury-gold/40 rounded-xl p-4 text-white shadow-2xl w-64">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-0.5">Live Weather</h3>
                  <p className="text-xl font-black text-white">Hattingen</p>
                </div>
                <div className="flex-shrink-0">
                  {getWeatherIcon(36)}
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-start mb-1">
                  <span className="text-5xl font-black text-luxury-gold leading-none">
                    {weather.temp}°
                  </span>
                  <span className="text-lg text-zinc-400 ml-1 mt-1">C</span>
                </div>
                <p className="text-base text-zinc-300 capitalize font-semibold mb-0.5">
                  {weather.description}
                </p>
                <p className="text-xs text-zinc-500">
                  {t.weather.feelsLike} {weather.feelsLike}°C
                </p>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent mb-3" />

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <div className="bg-luxury-gold/20 rounded-lg p-1.5">
                    <Droplets className="text-luxury-gold" size={14} />
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wide">{t.weather.humidity}</p>
                    <p className="text-sm font-bold">{weather.humidity}%</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="bg-luxury-gold/20 rounded-lg p-1.5">
                    <Wind className="text-luxury-gold" size={14} />
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wide">{t.weather.wind}</p>
                    <p className="text-sm font-bold">{weather.windSpeed} km/h</p>
                  </div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-luxury-gold/20">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-1.5 h-1.5 bg-luxury-gold rounded-full animate-pulse" />
                  <p className="text-[10px] text-luxury-gold font-bold uppercase tracking-wider">
                    Perfect Visit Weather
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Weather Widget - MOBILE (Mini) */}
      {weather && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="md:hidden absolute top-20 right-4 z-30"
        >
          <div className="bg-black/70 backdrop-blur-sm border border-luxury-gold/40 rounded-lg px-2.5 py-2 shadow-xl">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">
                {getWeatherIcon(20)}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-black text-luxury-gold leading-none">
                  {weather.temp}°
                </span>
              </div>
              <div className="h-4 w-px bg-luxury-gold/30" />
              <div className="text-[9px] text-zinc-400 leading-tight">
                <p className="font-bold">Hattingen</p>
                <p className="capitalize">{weather.description}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Content Overlay */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="max-w-5xl"
        >
          <motion.h1
            key={activeSection}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-8xl font-display font-bold text-white mb-6 text-shadow-lg"
          >
            {currentContent.title}
          </motion.h1>

          <motion.p
            key={`subtitle-${activeSection}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-3xl font-serif text-luxury-cream mb-4 text-shadow"
          >
            {currentContent.subtitle}
          </motion.p>

          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '200px' }}
            transition={{ duration: 1, delay: 0.8 }}
            className="h-1 bg-luxury-gold mx-auto mb-12"
          />

          {activeSection === 'hotel' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <button
                onClick={() => openBooking && openBooking('room')}
                className="btn-primary rounded-full px-12 py-5 text-lg shadow-2xl hover:shadow-luxury-gold/50 cursor-pointer"
              >
                {t.hotel.bookRoom}
              </button>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center text-white cursor-pointer"
            onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="text-sm mb-2 font-medium">Scroll to Explore</span>
            <ChevronDown size={32} className="text-luxury-gold" />
          </motion.div>
        </motion.div>

        {/* No slide indicators needed - single image per section */}
      </div>

      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute top-20 right-10 w-20 h-20 border-2 border-luxury-gold/30 rounded-full hidden lg:block"
      />
      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute bottom-40 left-10 w-32 h-32 border-2 border-luxury-gold/20 rounded-full hidden lg:block"
      />
    </section>
  );
};

export default Hero;
